import { useNavigate } from 'react-router-dom';
import './index.scss';
import { t } from 'i18next';
import {
    ButtonPrimary,
    Callout,
    Divider,
    IconInformationUserRegular,
    Row,
    RowList,
    Select,
    skinVars,
    Text,
    Text1,
    Text2,
    TextField,
    Title3,
    useWindowSize,
    ButtonFixedFooterLayout,
    IconBillQueriesEuroLight,
    Spinner,
} from '@telefonica/mistica';
import { useBillsContext } from '../../../../../context/bills-context/BillsContext';
import { getConsumptionName, getIcon, getUnits } from '../../../utils/functions';
import { claimIconsMap } from '../../../utils/constants/claims';
import { formatMobileNumber, formatPrices } from '../../../../../utils/dataTransformation/unitsConversion';
import { useEffect, useState } from 'react';
import { ContactOptions, IClaimConcept, IClaimContactInfo } from '../../../../../models/Bills.interfaces';
import { getFormatDate } from '../../../../../utils/dataTransformation/dateConversions';
import HeaderComponent from '../../../../../components/HeaderComponent';
import { createClaim, getClaimContacts } from '../../../../../services/BillService';
import { AxiosError, AxiosResponse } from 'axios';
import { IMisticaDropdownOptions } from '../../../../../models/interfaces';
import dayjs from 'dayjs';
import { setInfoError } from '../../../../../utils/dataTransformation/errorMesssages';

const ClaimSummary = () => {
    const navigate = useNavigate();
    const [commentError, setCommentError] = useState(false);
    const [contactType, setContactType] = useState<ContactOptions>(ContactOptions.sms);
    const [contactSMS, setContactSMS] = useState<IMisticaDropdownOptions[]>([]);
    const [contactPhones, setContactPhones] = useState<IMisticaDropdownOptions[]>([]);
    const [contactPersons, setContactPersons] = useState<IMisticaDropdownOptions[]>([]);
    const [contactEmails, setContactEmails] = useState<IMisticaDropdownOptions[]>([]);
    const [contactInfo, setContactInfo] = useState<{
        contactPerson: {
            idContactMedium: string;
            value: string;
            type: string;
            partyId: string;
            interactionRoleTypeId: string;
        };
        contactPhone: {
            idContactMedium: string;
            value: string;
            type: string;
            partyId: string;
            interactionRoleTypeId: string;
        };
        contact: {
            idContactMedium: string;
            value: string;
            type: string;
            partyId: string;
            interactionRoleTypeId: string;
        };
    }>({
        contactPerson: { idContactMedium: '', value: '', type: '', partyId: '', interactionRoleTypeId: '' },
        contactPhone: { idContactMedium: '', value: '', type: '', partyId: '', interactionRoleTypeId: '' },
        contact: { idContactMedium: '', value: '', type: '', partyId: '', interactionRoleTypeId: '' },
    });
    const [selectedClaims, setSelectedClaims] = useState<IClaimConcept[]>([]);
    const { bill, billClaimsSelected, claims, claimComment, setClaimComment, claimType, customerAccount } =
        useBillsContext();
    const size = useWindowSize();
    const [firstLoading, setfirstLoading] = useState(true);

    const contactTypes = Object.values(ContactOptions).map((data) => ({ text: t(data), value: data }));

    useEffect(() => {
        setClaimComment('');
        getContactInfo();
    }, []);

    useEffect(() => {
        setSelectedClaims(claims.filter((claim) => billClaimsSelected.has(claim.id)));
    }, []);

    const getContactInfo = async () => {
        try {
            const response: AxiosResponse = await getClaimContacts();
            manageContactInfo(response.data.contacts);
            setfirstLoading(false);
        } catch (error) {
            setfirstLoading(false);
            console.error(error);
        }
    };

    const manageContactInfo = (data: IClaimContactInfo[]) => {
        const tempContactSMS: IMisticaDropdownOptions[] = [];
        const tempContactEmails: IMisticaDropdownOptions[] = [];
        const tempContactPeople: IMisticaDropdownOptions[] = [];
        const tempContactPhones: IMisticaDropdownOptions[] = [];

        const contactSMSSet = new Set();
        const contactPhonesSet = new Set();
        const contactEmailsSet = new Set();

        const phonesType3 = data.filter((item) => item.interactionRoleTypeId === '3');

        const uniquePhones: { [value: string]: IClaimContactInfo } = {};
        phonesType3.forEach((item) => {
            uniquePhones[item.value] = item;
        });

        Object.values(uniquePhones).forEach((item) => {
            tempContactPhones.push({
                value: item.id,
                text: formatMobileNumber(item.value),
                data: {
                    user: item.contactPerson,
                    type: item.type,
                    partyId: item.partyId,
                    interactionRoleTypeId: item.interactionRoleTypeId,
                },
            });
        });

        data.forEach((item) => {
            const key = `${item.id}${item.value}`;

            if (!tempContactPeople.some((element) => element.text === item.contactPerson))
                tempContactPeople.push({
                    value: item.id,
                    text: item.contactPerson,
                    data: { type: item.type },
                });

            switch (item.type) {
                case 'SMS_':
                    if (!contactSMSSet.has(key)) {
                        tempContactSMS.push({
                            value: item.id,
                            text: formatMobileNumber(item.value),
                            data: {
                                type: item.type,
                                partyId: item.partyId,
                                interactionRoleTypeId: item.interactionRoleTypeId,
                            },
                        });
                        contactSMSSet.add(key);
                    }
                    if (item.interactionRoleTypeId === '3' && !contactPhonesSet.has(key)) {
                        tempContactPhones.push({
                            value: item.id,
                            text: formatMobileNumber(item.value),
                            data: {
                                user: item.contactPerson,
                                type: item.type,
                                partyId: item.partyId,
                                interactionRoleTypeId: item.interactionRoleTypeId,
                            },
                        });
                        contactPhonesSet.add(key);
                    }
                    break;
                case 'EMAI':
                    if (!contactEmailsSet.has(key)) {
                        tempContactEmails.push({
                            value: item.id,
                            text: item.value,
                            data: {
                                type: item.type,
                                partyId: item.partyId,
                                interactionRoleTypeId: item.interactionRoleTypeId,
                            },
                        });
                        contactEmailsSet.add(key);
                    }
                    break;
                case 'TELF':
                    if (!contactPhonesSet.has(key)) {
                        tempContactPhones.push({
                            value: item.id,
                            text: formatMobileNumber(item.value),
                            data: {
                                user: item.contactPerson,
                                type: item.type,
                                partyId: item.partyId,
                                interactionRoleTypeId: item.interactionRoleTypeId,
                            },
                        });
                        contactPhonesSet.add(key);
                    }
                    break;
            }
        });

        setContactPersons(tempContactPeople);
        setContactPhones(tempContactPhones);
        setContactSMS(tempContactSMS);
        setContactEmails(tempContactEmails);

        const filteredPhones = tempContactPhones.filter(
            (phone, idx, arr) =>
                phone.data.interactionRoleTypeId === '3' && arr.findIndex((p) => p.text === phone.text) === idx,
        );
        let defaultPhone: IClaimContactInfo | undefined;
        if (filteredPhones.length === 1) {
            defaultPhone = data.find(
                (item) => item.id === filteredPhones[0].value && item.interactionRoleTypeId === '3',
            );
        } else {
            defaultPhone = data.find((item) => item.interactionRoleTypeId === '3');
        }
        setContactInfo((prev) => ({
            ...prev,
            contact: {
                value: tempContactSMS[0].text,
                idContactMedium: tempContactSMS[0].value,
                type: tempContactSMS[0].data.type,
                partyId: tempContactSMS[0].data.partyId,
                interactionRoleTypeId: tempContactSMS[0].data.interactionRoleTypeId ?? '',
            },
            contactPerson: {
                value: tempContactPeople[0].text,
                idContactMedium: tempContactPeople[0].value,
                type: tempContactPeople[0].data.type,
                partyId: tempContactPeople[0].data.partyId,
                interactionRoleTypeId: tempContactPeople[0].data.interactionRoleTypeId ?? '',
            },
            contactPhone: defaultPhone
                ? {
                      value: defaultPhone.value,
                      idContactMedium: defaultPhone.id,
                      type: defaultPhone.type,
                      partyId: defaultPhone.partyId,
                      interactionRoleTypeId: defaultPhone.interactionRoleTypeId,
                  }
                : { idContactMedium: '', value: '', type: '', partyId: '', interactionRoleTypeId: '' },
        }));
    };

    const navigationBackHandler = () => navigate('/claims/claims-selection');

    const changeCommentHandler = (value: string) => {
        setClaimComment(value);
        if (value.trim().length === 0) {
            setCommentError(true);
        } else if (value.trim().length > 0) {
            setCommentError(false);
        }
    };

    const changeContactTypeHandler = (value: string) => {
        setContactType(Object(ContactOptions)[value]);
        if (value === 'sms') {
            setContactInfo((prevState) => ({
                ...prevState,
                contact: {
                    idContactMedium: contactSMS[0].value,
                    value: contactSMS[0].text,
                    type: contactSMS[0].data.type,
                    partyId: contactSMS[0].data.partyId,
                    interactionRoleTypeId: contactSMS[0].data.interactionRoleTypeId ?? '',
                },
            }));
        } else {
            setContactInfo((prevState) => ({
                ...prevState,
                contact: {
                    idContactMedium: contactEmails[0].value,
                    value: contactEmails[0].text,
                    type: contactEmails[0].data.type,
                    partyId: contactEmails[0].data.partyId,
                    interactionRoleTypeId: contactEmails[0].data.interactionRoleTypeId ?? '',
                },
            }));
        }
    };

    const changeContactHandler = (value: string) => {
        const selectedContact = (contactType === 'sms' ? contactSMS : contactEmails).find(
            (contact) => contact.value === value,
        )!;
        setContactInfo((prevState) => ({
            ...prevState,
            contact: {
                type: selectedContact.data.type,
                value: selectedContact.text,
                idContactMedium: selectedContact.value,
                partyId: selectedContact.data.partyId,
                interactionRoleTypeId: selectedContact.data.interactionRoleTypeId ?? '',
            },
        }));
    };

    const changePhoneHandler = (value: string) => {
        const selectedPhone = contactPhones.find((phone) => phone.value === value)!;
        setContactInfo((prevState) => ({
            ...prevState,
            contactPhone: {
                type: selectedPhone.data.type,
                value: selectedPhone.text,
                idContactMedium: selectedPhone.value,
                partyId: selectedPhone.data.partyId,
                interactionRoleTypeId: selectedPhone.data.interactionRoleTypeId,
            },
        }));
    };

    const changeContactPersonHandler = (value: string) => {
        const tempContactPerson = contactPersons.find((person) => person.value === value)!;
        setContactInfo((prevState) => ({
            ...prevState,
            contactPerson: {
                type: tempContactPerson.data.type,
                value: tempContactPerson.text,
                idContactMedium: tempContactPerson.value,
                partyId: tempContactPerson.data.partyId,
                interactionRoleTypeId: tempContactPerson.data.interactionRoleTypeId ?? '',
            },
        }));
    };

    const saveHandler = () => {
        if (!claimComment.trim()) {
            setCommentError(true);
            return;
        } else {
            createClaim(createClaimObject())
                .then((response) => {
                    navigate('/claims/feedback-claim-opening', {
                        state: { successFeedback: true, claimCode: response.data },
                    });
                })
                .catch((err: AxiosError) => {
                    navigate('/claims/feedback-claim-opening', {
                        state: { successFeedback: false, err: setInfoError(err) },
                    });
                });
        }
    };

    const createClaimObject = () => {
        return {
            economicCustomerProblemCLT: {
                customerComments: claimComment,
                interactionRoles: Object.entries(contactInfo)
                    .filter(([key]) => key !== 'contactPerson')
                    .map(([_, contact]) => ({
                        idPartyRole: contact.partyId,
                        idcontactMedium: contact.idContactMedium,
                        type: contact.interactionRoleTypeId,
                    })),
                claimedCustomerAccounts: [
                    {
                        customerAccount: customerAccount,
                        claimedCustomerBills: [
                            {
                                billNo: bill.id,
                                disputedAmountCreateds: selectedClaims.map((claim) => ({
                                    idMotive: claim.idMotive,
                                    idSubmotive: claim.idSubmotive,
                                    disputedAmount: `-${claim.amount.toString()}`,
                                    appliedCustomerBillingRate: claim.id,
                                })),
                            },
                        ],
                    },
                ],
            },
        };
    };

    if (firstLoading) {
        return (
            <div className='c-spinner'>
                <Spinner size={24} />
                <span style={{ background: 'white' }} className='c-spinner-overlay'></span>
            </div>
        );
    }

    const filteredContactTypes = contactTypes.filter((option) => {
        if (option.value === ContactOptions.email) {
            return contactEmails.some((item) => item.data.interactionRoleTypeId === '2' && item.data.type === 'EMAI');
        }
        if (option.value === ContactOptions.sms) {
            return contactSMS.some((item) => item.data.interactionRoleTypeId === '2' && item.data.type === 'SMS_');
        }
        return false;
    });

    const mainContent = (
        <>
            <div className='claim-summary__body'>
                <div className='claim-summary__title'>
                    <Title3>{t('claimSummaryTitle')}</Title3>
                </div>
                <Text color={skinVars.colors.textSecondary}>{`${t('billIssueDate')} ${dayjs(bill?.issueDate).format(
                    'DD/MM/YYYY',
                )}`}</Text>
                <div className='claim-summary__body-divider'>
                    <Divider />
                </div>
                <div className='claim-summary__content'>
                    <div className='claim-summary__selected-claims'>
                        <div className='claim-summary__section-title'>
                            <Text1 color={skinVars.colors.textSecondary} weight='regular'>
                                {t('claimSummarySelectedConceptsTitle').toUpperCase()}
                            </Text1>
                            <RowList>
                                {selectedClaims.map((claim) => {
                                    return (
                                        <div key={claim.id} className='claim-summary__claim'>
                                            <div className='claim-summary__claim-info'>
                                                <Row
                                                    title={
                                                        claimType === 'quota' ? claim.text! : getConsumptionName(claim)
                                                    }
                                                    description={
                                                        claimType === 'additional'
                                                            ? `${
                                                                  claim.appliedCustomerBillingRateCharDTOOUTs.find(
                                                                      (item: any) => item.name === 'UsageTime',
                                                                  )?.value
                                                              } - ${getUnits(claim)}`
                                                            : ''
                                                    }
                                                    asset={
                                                        claimType === 'quota' ? (
                                                            <IconBillQueriesEuroLight size={24} />
                                                        ) : (
                                                            getIcon(
                                                                claim.typeId ?? '',
                                                                claimIconsMap,
                                                                claim?.product?.identifier ?? '',
                                                            )
                                                        )
                                                    }
                                                />
                                                <div className='claim-summary__claim-reasons'>
                                                    <div>
                                                        <Text2 weight='regular' color={skinVars.colors.textPrimary}>
                                                            {t('dateText') + ':'}
                                                        </Text2>
                                                        <Text2 weight='regular' color={skinVars.colors.textSecondary}>
                                                            {' '}
                                                            {claimType === 'quota'
                                                                ? `${getFormatDate(
                                                                      claim.startDate,
                                                                      'DD M',
                                                                  )} a ${getFormatDate(claim.endDate, 'DD M')}`
                                                                : getFormatDate(claim.starDate, 'DD/MM/YYYY')}
                                                        </Text2>
                                                    </div>
                                                    <div>
                                                        <Text2 weight='regular' color={skinVars.colors.textPrimary}>
                                                            {t('claimSummaryReason')}:
                                                        </Text2>
                                                        <Text2 weight='regular' color={skinVars.colors.textSecondary}>
                                                            {' '}
                                                            {claim.motive}
                                                        </Text2>
                                                    </div>
                                                    <div>
                                                        <Text2 weight='regular' color={skinVars.colors.textPrimary}>
                                                            {t('claimSummarySecondaryReason')}:
                                                        </Text2>
                                                        <Text2 weight='regular' color={skinVars.colors.textSecondary}>
                                                            {' '}
                                                            {claim.subMotive}
                                                        </Text2>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='claim-summary__claim-cost'>
                                                <Text color={skinVars.colors.textPrimary}>
                                                    {formatPrices(claim.amount, 'spent')}
                                                </Text>
                                            </div>
                                        </div>
                                    );
                                })}
                            </RowList>
                        </div>
                    </div>
                    <div className='claim-summary__reason'>
                        <div className='claim-summary__section-title'>
                            <Text1 color={skinVars.colors.textSecondary} weight='regular'>
                                {t('claimSummaryReasonsTitle').toUpperCase()}
                            </Text1>
                        </div>
                        <TextField
                            multiline
                            helperText={t('claimSummaryReasonHelperText')}
                            label={t('claimSummaryReasonLabel')}
                            value={claimComment}
                            onChangeValue={changeCommentHandler}
                            name={t('claimSummaryReasonLabel')}
                            maxLength={1500}
                            error={commentError}
                        />
                    </div>
                    <div className='claim-summary__contact'>
                        <div className='claim-summary__section-title'>
                            <Text1 color={skinVars.colors.textSecondary} weight='regular'>
                                {t('claimSummaryContactTitle').toUpperCase()}
                            </Text1>
                        </div>
                        <div className='claim-summary__selectors'>
                            <Select
                                label={t('contactType')}
                                name={t('contactType')}
                                options={filteredContactTypes}
                                value={contactType}
                                onChangeValue={changeContactTypeHandler}
                            />
                            <Select
                                label={t(contactType)}
                                name={t(contactType)}
                                options={contactType === 'sms' ? contactSMS : contactEmails}
                                value={contactInfo.contact.idContactMedium}
                                onChangeValue={changeContactHandler}
                            />
                        </div>
                    </div>
                    <div className='claim-summary__phone-notifications'>
                        <div className='claim-summary__section-title'>
                            <Text1 color={skinVars.colors.textSecondary} weight='regular'>
                                {t('claimSummaryNotificationsTitle').toUpperCase()}
                            </Text1>
                        </div>
                        <div className='claim-summary__selectors'>
                            <Select
                                label={t('contactPerson')}
                                name={t('contactPerson')}
                                options={contactPersons}
                                value={contactInfo.contactPerson.idContactMedium}
                                onChangeValue={changeContactPersonHandler}
                            />
                            <Select
                                label={t('exploreBoxedRowTagMobilesTitle')}
                                name={t('exploreBoxedRowTagMobilesTitle')}
                                options={contactPhones.filter(
                                    (phone, idx, arr) =>
                                        phone.data.interactionRoleTypeId === '3' &&
                                        arr.findIndex((p) => p.text === phone.text) === idx,
                                )}
                                value={contactInfo.contactPhone.idContactMedium}
                                onChangeValue={changePhoneHandler}
                            />
                        </div>
                    </div>
                    <Callout
                        asset={<IconInformationUserRegular color={skinVars.colors.brand} size={24} />}
                        description={t('claimSummaryCallout')}
                    ></Callout>
                </div>
            </div>
        </>
    );

    const isConfirmEnabled =
        contactInfo.contact.idContactMedium &&
        contactInfo.contactPerson.idContactMedium &&
        contactInfo.contactPhone.idContactMedium &&
        selectedClaims.length > 0 &&
        selectedClaims.every((claim) => claim.idMotive && claim.idSubmotive);

    return (
        <section className='claim-summary'>
            <HeaderComponent
                title={t('claimSummaryHeader')}
                navigationBarOnBack={navigationBackHandler}
                breadcrumbs={[
                    { title: t('invoice'), url: '/bills' },
                    {
                        title: t('billDetail'),
                        url: `/bills/bill-detail?id=${bill.id}`,
                    },
                    {
                        title: t('billManagement'),
                        url: '/claims/management-invoice',
                    },
                    {
                        title: t('openClaim'),
                        url: '/claims/claim-type',
                    },
                ]}
            />
            {size.width >= 1024 ? (
                <>
                    {mainContent}
                    <div className='claim-summary__bottom'>
                        <ButtonPrimary role='onPress' onPress={saveHandler} disabled={!isConfirmEnabled}>
                            {t('confirm')}
                        </ButtonPrimary>
                    </div>
                </>
            ) : (
                <ButtonFixedFooterLayout
                    button={
                        <ButtonPrimary role='onPress' onPress={saveHandler} disabled={!isConfirmEnabled}>
                            {t('confirm')}
                        </ButtonPrimary>
                    }
                >
                    {mainContent}
                </ButtonFixedFooterLayout>
            )}
        </section>
    );
};

export default ClaimSummary;
